import { Box } from '@mui/material';
import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomCheckbox } from '../../../../../components/CustomCheckbox';
import { CustomSelect } from '../../../../../components/CustomSelect';
import { CustomTextField } from '../../../../../components/CustomTextField';
import DivContainerBody from '../../../../../components/DivContainerBody';
import DividerWithText from '../../../../../components/DividerWithText';
import { STATUS_MODEL } from '../../../../../enums/statusEnum';
import FinesSubviewApp from '../Functionality/FinesSubviewApp';
import { isMediaOptionChecked, MEDIA_OPTIONS } from '../Model/Model';
/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

const FinesSubview = forwardRef((props, ref) => {
    const {
        handleChange,
        emptyFields,
        item,
        handleCheckbox,
        updateData,
        TIME_UNIT_TRANS,
        UNIT_TYPE_TRANS,
        disabled,
        TIME_UNIT_NO_HOURS_TRANS
    } = FinesSubviewApp();

    const { t } = useTranslation();

    /** Expose internal function to parent through ref */
    useImperativeHandle(ref, () => ({
        updateData,
    }));

    return (
        <DivContainerBody>
            <div className='row'>
                <div className={'col-12 mt-4'}>

                    {/* Details */}
                    <DividerWithText title={t('details')} />
                    <p className='fs-14px ibl-font-w-600 mt-3 mb-3'>{t('warning_notices')}</p>
                </div>
                <Box className='col-sm-12 col-md-3'>
                    <p className='fs-14px ibl-font-w-400 mb-3'>{t('number_of_warnings_before_fine_is_reached')}</p>
                </Box>
                <Box className='col-sm-12 col-md-3'>
                    <CustomTextField
                        emptyFields={emptyFields}
                        id='txt_fs_warnings_count'
                        length={3}
                        name='warningsCount'
                        onChange={handleChange}
                        placeholder={t('enter_a_number')}
                        required={true}
                        trans='quantity'
                        value={item?.warningsCount}
                        type='number'
                        disabled={disabled}
                    />
                </Box>

                <div className={"col-12 mb-2"}>
                    {/* Notifications */}
                    <p className='fs-14px ibl-font-w-600'>{t('user_notification')}</p>
                </div>

                <div className='col-12 col-md-3 mb-3'>
                    <div className={"d-flex flex-column gap-0"}>
                        <CustomCheckbox
                            className='ibl-font-w-400 font-color-general-950'
                            id='cb_fs_email'
                            labelId='lbl_fs_email'
                            label='email'
                            trans='email'
                            name='showEmail'
                            onChange={() => handleCheckbox(MEDIA_OPTIONS.EMAIL)}
                            checked={isMediaOptionChecked(item.mediaOptions, MEDIA_OPTIONS.EMAIL, STATUS_MODEL.ACTIVE_COMPANY)}
                            marginbutton={true}
                            disabled={disabled}
                        />

                        <CustomCheckbox
                            className='ibl-font-w-400 font-color-general-950'
                            id='cb_fs_app_notification'
                            labelId='lbl_fs_app_notification'
                            label='show_app_notification'
                            trans='show_app_notification'
                            name='showAppNotification'
                            onChange={() => handleCheckbox(MEDIA_OPTIONS.APP_NOTIFICATION)}
                            checked={isMediaOptionChecked(item.mediaOptions, MEDIA_OPTIONS.APP_NOTIFICATION, STATUS_MODEL.ACTIVE_COMPANY)}
                            marginbutton={true}
                            disabled={disabled}
                        />

                        <CustomCheckbox
                            className='ibl-font-w-400 font-color-general-950'
                            id='cb_fs_sms_message'
                            labelId='lbl_fs_sms_message'
                            label='sms_message'
                            trans='sms_message'
                            name='smsMessage'
                            onChange={() => handleCheckbox(MEDIA_OPTIONS.SMS_MESSAGE)}
                            checked={isMediaOptionChecked(item.mediaOptions, MEDIA_OPTIONS.SMS_MESSAGE, STATUS_MODEL.ACTIVE_COMPANY)}
                            marginbutton={true}
                            disabled={disabled}
                        />
                    </div>

                </div>

                {/* Fine ruling */}
                <div className={"col-12 mb-4"}>
                    <DividerWithText title={t('fine_ruling')} />
                </div>

                <div className='row'>
                    <Box className='col-12 col-md-3 pl-0'>
                        <p className='fs-14px ibl-font-w-400'>{t('first_time_fine_amount')}</p>
                    </Box>
                    <Box className='col-12 col-md-3 pl-0'>
                        <CustomTextField
                            required={true}
                            placeholder={t('enter_amount')}
                            id='txt_fs_first_time_fine_amount'
                            name='firstTimeFineAmount'
                            trans='amount'
                            length={4}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            value={item?.firstTimeFineAmount}
                            type='number'
                            disabled={disabled}
                        />
                    </Box>
                </div>
                <div className='row'>
                    <Box className='col-12 col-md-3 pl-0'>
                        <p className='fs-14px ibl-font-w-400'>{t('standard_fine_amount')}</p>
                    </Box>
                    <Box className='col-12 col-md-3 pl-0'>
                        <CustomTextField
                            required={true}
                            placeholder={t('enter_standard_amount')}
                            id='txt_fs_standard_amount'
                            name='standardFineAmount'
                            trans='amount'
                            length={4}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            value={item?.standardFineAmount}
                            type='number'
                            disabled={disabled}
                        />
                    </Box>
                </div>

                <div className='row'>
                    <Box className='col-12 col-md-3 pl-0'>
                        <p className='fs-14px ibl-font-w-400'>{t('fines_before_recurrence_applies')}</p>
                    </Box>
                    <Box className='col-12 col-md-3 pl-0'>
                        <CustomTextField
                            required={true}
                            placeholder={t('enter_a_number')}
                            id='txt_fs_fines_before_recurrence'
                            name='finesBeforeRecurrence'
                            trans='quantity'
                            length={3}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            value={item?.finesBeforeRecurrence}
                            type='number'
                            disabled={disabled}
                        />
                    </Box>
                </div>

                {/* Recurernce penalty type */}
                <div className='row'>
                    <Box className='col-12 col-md-3 pl-0'>
                        <p className='fs-14px ibl-font-w-'>{t('recurrence_penalty_type')}</p>
                    </Box>
                    <Box className='col-12 col-md-3 pl-0'>
                        <CustomSelect
                            id={'slt_fs_recurrence_penalty_type'}
                            name={'recurrencePenaltyType'}
                            value={item.recurrencePenaltyType}
                            keyName='id'
                            onChange={handleChange}
                            list={UNIT_TYPE_TRANS}
                            trans={'unit_type'}
                            required={true}
                            showNoSelectionOption={false}
                            disabled={disabled}
                        />
                    </Box>
                    <Box className='col-12 col-md-3'>
                        <CustomTextField
                            id='txt_fs_recurrence_penalty_value'
                            required={true}
                            placeholder={t('enter_quantity')}
                            name='recurrencePenaltyValue'
                            trans='amount'
                            length={4}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            value={item?.recurrencePenaltyValue}
                            type='number'
                            disabled={disabled}
                        />
                    </Box>
                </div>


                {/* Recurrence coldown interval */}
                <div className='row'>
                    <Box className='col-12 col-md-3 pl-0'>
                        <p className='fs-14px ibl-font-w-400 mb-3'>{t('recurrence_cooldown_interval')}</p>
                    </Box>
                    <Box className='col-12 col-md-3 pl-0'>
                        <CustomTextField
                            id='txt_fs_recurrence_cooldown_value'
                            required={true}
                            placeholder={t('enter_a_number')}
                            name='recurrenceCooldownValue'
                            trans='quantity'
                            length={3}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            value={item?.recurrenceCooldownValue}
                            type='number'
                            disabled={disabled}
                        />
                    </Box>
                    <Box className='col-12 col-md-3'>
                        <CustomSelect
                            emptyFields={emptyFields}
                            id={'slt_fs_recurrence_cooldown_unit'}
                            keyName='id'
                            list={TIME_UNIT_TRANS}
                            name={'recurrenceCooldownUnit'}
                            onChange={handleChange}
                            placeholder={'select_a_unit_of_time'}
                            required={true}
                            showNoSelectionOption={false}
                            trans={'time_unit'}
                            value={item?.recurrenceCooldownUnit}
                            disabled={disabled}
                        />
                    </Box>
                </div>

                {/* Discount conditions */}
                <div className={"col-12"}>
                    <DividerWithText title={t('discount_conditions')} />
                </div>
                <div className='row mt-4 '>
                    <div className='col-12 col-md-3 pl-0'>
                        <p className='fs-14px ibl-font-w-400 mb-3'>{t('max_time_with_discount_apply')}</p>
                    </div>

                    {/*  Maximum time to apply discount*/}
                    <div className='col-12 col-md-3 pl-0'>
                        <CustomTextField
                            id='txt_fs_max_discount_period'
                            required={true}
                            placeholder={t('enter_a_number')}
                            name='maxDiscountPeriod'
                            trans='quantity'
                            length={3}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            value={item?.maxDiscountPeriod}
                            type='number'
                            disabled={disabled}
                        />
                    </div>
                    <div className='col-12 col-md-3'>
                        <CustomSelect
                            emptyFields={emptyFields}
                            id={'slt_fs_max_discount_unit'}
                            keyName='id'
                            list={TIME_UNIT_NO_HOURS_TRANS}
                            name={'maxDiscountUnit'}
                            onChange={handleChange}
                            placeholder={'select_a_unit_of_time'}
                            required={true}
                            showNoSelectionOption={false}
                            trans={'time_unit'}
                            value={item?.maxDiscountUnit}
                            disabled={disabled}
                        />
                    </div>
                </div>
                <div className='row mb-3'>
                    <div className='col-12 col-md-3 pl-0'>
                        <p className='fs-14px ibl-font-w-400 mb-3'>{t('early_payment_discount')}</p>
                    </div>

                    {/*  Early payment discount */}
                    <div className='col-12 col-md-3 pl-0'>
                        <CustomSelect
                            emptyFields={emptyFields}
                            id={'slt_fs_early_payment_discount_type'}
                            keyName='id'
                            list={UNIT_TYPE_TRANS}
                            name={'earlyPaymentDiscountType'}
                            onChange={handleChange}
                            placeholder={'select_a_unit_of_time'}
                            required={true}
                            showNoSelectionOption={false}
                            trans={'unit_type'}
                            value={item?.earlyPaymentDiscountType}
                            disabled={disabled}
                        />
                    </div>
                    <div className='col-12 col-md-3'>
                        <CustomTextField
                            required={true}
                            placeholder={t('enter_quantity')}
                            id='txt_fs_early_payment_discount_value'
                            name='earlyPaymentDiscountValue'
                            trans='amount'
                            length={4}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            value={item?.earlyPaymentDiscountValue}
                            type='number'
                            disabled={disabled}
                        />
                    </div>
                </div>

                <div className={"col-12"}>
                    <DividerWithText title={t('suspensions_conditions')} />
                </div>
                <div className='row mt-4 mb-5'>
                    <div className='col-12 col-md-3 pl-0'>
                        <p className='fs-14px ibl-font-w-400 mb-3'>{t('max_time_without_payment_before_suspension')}</p>
                    </div>


                    {/* Maximum time without payment before suspensio */}
                    <div className='col-12 col-md-3 pl-0'>
                        <CustomTextField
                            required={true}
                            placeholder={t('enter_a_number')}
                            id='txt_fs_suspension_grace_period'
                            name='suspensionGracePeriod'
                            trans='quantity'
                            length={3}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            value={item?.suspensionGracePeriod}
                            type='number'
                            disabled={disabled}
                            inputProps={{ min: 1 }}
                            onKeyDown={(e) => {
                                if (e.key === '0' && !e.target.value) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </div>
                    <div className='col-12 col-md-3'>
                        <CustomSelect
                            emptyFields={emptyFields}
                            id={'slt_fs_suspension_grace_unit'}
                            keyName='id'
                            list={TIME_UNIT_NO_HOURS_TRANS}
                            name={'suspensionGraceUnit'}
                            onChange={handleChange}
                            placeholder={'select_a_unit_of_time'}
                            required={true}
                            showNoSelectionOption={false}
                            trans={'time_unit'}
                            value={item?.suspensionGraceUnit}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>
        </DivContainerBody>
    );
});

export default FinesSubview;
